import React from "react";
import { Box, Button } from "@mui/joy";
import { HistoryIcon } from "../../styles/StyledMUI";
import type { TheoryTest, TheoryTestTemplate } from "../../api/theoryTests/TheoryTest.typed";
import { usePortal } from "../../contexts/api";
import TheoryTestHistoryModal from "./TheoryTestHistoryModal";
import DarkTypography from "../common/DarkTypography";
import { ctaAccentSx } from "../../styles/globalStyles";

interface TheoryTestHistoryProps {
  tests: TheoryTest[];
  trainingTemplateId?: number;
}

export default function TheoryTestHistory({ tests, trainingTemplateId }: TheoryTestHistoryProps) {
  const { getPublicTheoryTestTemplate } = usePortal();
  const [open, setOpen] = React.useState(false);
  const [template, setTemplate] = React.useState<TheoryTestTemplate | null>(null);

  React.useEffect(() => {
    if (!trainingTemplateId || trainingTemplateId <= 0) return;
    let cancelled = false;
    void getPublicTheoryTestTemplate(String(trainingTemplateId)).then((data) => {
      if (!cancelled) setTemplate(data ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [trainingTemplateId, getPublicTheoryTestTemplate]);

  if (!tests || tests.length === 0) return null;

  const bestAttempt = tests.reduce((prev, current) =>
    current.Result > prev.Result ? current : prev,
  );

  return (
    <>
      <Box sx={{ mt: 2 }}>
        <Box
          sx={{
            display: { xs: "flex", sm: "none" },
            flexDirection: "column",
            gap: 2,
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              maxWidth: "320px",
            }}
          >
            <DarkTypography level="body-md" colorVariant="secondary">
              Tentatives : {tests.length}
            </DarkTypography>
            <DarkTypography level="body-md" colorVariant="secondary">
              Meilleure note :{" "}
              {(bestAttempt.Result / 100).toLocaleString("fr-FR", {
                style: "percent",
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}
            </DarkTypography>
          </Box>
          <Button
            size="sm"
            variant="soft"
            onClick={() => setOpen(true)}
            aria-label="Voir tous les résultats"
            startDecorator={<HistoryIcon sx={{ fontSize: "1.1rem" }} />}
            sx={[ctaAccentSx as object, { borderRadius: "8px", fontSize: "12px" }]}
          >
            Voir tous les résultats
          </Button>
        </Box>

        <Box
          sx={{
            display: { xs: "none", sm: "flex" },
            alignItems: "center",
            justifyContent: "center",
            gap: 3,
          }}
        >
          <DarkTypography level="body-md" colorVariant="secondary">
            Tentatives : {tests.length}
          </DarkTypography>
          <DarkTypography level="body-md" colorVariant="secondary">
            Meilleure note :{" "}
            {(bestAttempt.Result / 100).toLocaleString("fr-FR", {
              style: "percent",
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })}
          </DarkTypography>
          <Button
            size="sm"
            variant="soft"
            onClick={() => setOpen(true)}
            aria-label="Voir tous les résultats"
            startDecorator={<HistoryIcon sx={{ fontSize: "1.1rem" }} />}
            sx={[ctaAccentSx as object, { borderRadius: "8px", fontSize: "12px" }]}
          >
            Voir tous les résultats
          </Button>
        </Box>
      </Box>

      <TheoryTestHistoryModal
        tests={tests}
        template={template}
        isOpen={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
